import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Post, Comment, postService } from '../../../services/postService';
import { User } from '../../../types';
import { Button } from '../../../components/ui/button';
import { Card, CardContent, CardFooter, CardHeader } from '../../../components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '../../../components/ui/avatar';
import { getMediaUrl } from '../../../utils/media';
import { useToast } from '../../../components/Toast';
import { useAuth } from '../../../hooks/useAuth';
import { Badge } from '../../../components/ui/badge';

interface ProfilePostsProps {
    posts: Post[];
    loading: boolean;
    setPosts: React.Dispatch<React.SetStateAction<Post[]>>;
    user: User | null;
}

export const ProfilePosts: React.FC<ProfilePostsProps> = ({ posts, loading, setPosts, user }) => {
    const { t, i18n } = useTranslation(['profile', 'common']);
    const { showToast } = useToast();
    const { user: currentUser } = useAuth();

    // Local state for interactions could go here (e.g. comment input per post if inline)
    // For now, mirroring simple structure.

    const handleLike = async (postId: number) => {
        try {
            const result = await postService.toggleLikePost(postId);
            setPosts(prev => prev.map(p =>
                p.id === postId ? { ...p, is_liked: result.is_liked, likes_count: result.likes_count } : p
            ));
        } catch (error) {
            console.error(error);
        }
    };

    const formatDate = (dateString: string) => {
        const date = new Date(dateString);
        return date.toLocaleDateString(i18n.language, { month: 'short', day: 'numeric', year: 'numeric' });
    };

    if (loading) {
        return <div className="text-center py-8 text-gray-500">{t('contributionList.loadingPosts')}</div>;
    }

    if (posts.length === 0) {
        return (
            <Card className="bg-gray-50 border-dashed">
                <CardContent className="flex flex-col items-center justify-center py-12 text-center">
                    <div className="text-4xl mb-4">📝</div>
                    <h3 className="text-lg font-medium text-gray-900">{t('contributionList.empty')}</h3>
                    <p className="text-gray-500 mt-1">{t('contributionList.emptyDesc')}</p>
                </CardContent>
            </Card>
        );
    }

    return (
        <div className="space-y-6">
            <h2 className="text-xl font-bold mb-4">{t('contributionList.title', { count: posts.length })}</h2>
            {posts.map(post => (
                <Card key={post.id} className="overflow-hidden shadow-sm hover:shadow-md transition-shadow">
                    <CardHeader className="p-4 pb-2 flex flex-row space-y-0 gap-3">
                        <Avatar className="w-10 h-10">
                            <AvatarImage src={getMediaUrl(post.author.avatar)} />
                            <AvatarFallback>{(post.author.full_name || post.author.username || 'U')[0].toUpperCase()}</AvatarFallback>
                        </Avatar>
                        <div className="flex-1">
                            <div className="flex justify-between items-start">
                                <div>
                                    <span className="font-semibold text-gray-900">{post.author.full_name || post.author.username}</span>
                                    {post.author.role === 'admin' && (
                                        <Badge variant="secondary" className="ml-2 text-xs bg-red-100 text-red-600">Admin</Badge>
                                    )}
                                </div>
                                <span className="text-xs text-gray-500">{formatDate(post.created_at)}</span>
                            </div>
                            {/* Tags or Category if any */}
                            {post.category && (
                                <Badge variant="outline" className="text-xs mt-1 font-normal text-gray-500">
                                    {post.category.name}
                                </Badge>
                            )}
                        </div>
                    </CardHeader>
                    <CardContent className="p-4 pt-2">
                        <h3 className="font-medium text-lg mb-2">{post.title}</h3>
                        <div className="text-gray-700 whitespace-pre-wrap text-sm line-clamp-3 mb-3">
                            {post.content}
                        </div>

                        {/* Media Previews (Simplified) */}
                        {/* Media Previews (Simplified) */}
                        {post.thumbnail_url && (
                            <div className="rounded-lg overflow-hidden my-2 max-h-60 bg-gray-100 flex justify-center">
                                <img src={getMediaUrl(post.thumbnail_url)} alt="Attachment" className="h-full object-contain" />
                            </div>
                        )}
                        {post.video_url && (
                            <div className="flex items-center gap-2 p-2 bg-gray-50 rounded text-sm text-blue-600">
                                <span>🎥</span> {t('contributionList.viewVideo')}
                            </div>
                        )}
                    </CardContent>
                    <CardFooter className="p-4 pt-0 border-t bg-gray-50/50 flex justify-between items-center text-sm text-gray-600">
                        <div className="flex gap-4 mt-2">
                            <button
                                onClick={() => handleLike(post.id)}
                                className={`flex items-center gap-1 hover:text-red-500 transition-colors ${post.is_liked ? 'text-red-500' : ''}`}
                            >
                                <span>{post.is_liked ? '❤️' : '🤍'}</span>
                                <span>{post.likes_count || 0}</span>
                            </button>
                            <button className="flex items-center gap-1 hover:text-blue-500 transition-colors">
                                <span>💬</span>
                                <span>{post.comments_count || 0}</span>
                            </button>
                        </div>
                        <Button variant="ghost" size="sm" className="text-xs">
                            {t('contributionList.readMore')}
                        </Button>
                    </CardFooter>
                </Card>
            ))}
        </div>
    );
};
